'use client';

import React, { createContext, useContext, ReactNode } from 'react';
import { EntityContextType, useAuthStore } from '@/stores/authStore';

interface EntityPrivilegeContextType {
  /** Current entity ID */
  entityId: string | null;
  /** Current entity type */
  entityType: EntityContextType | null;
  /** Load entity privileges for the current entity */
  loadEntityPrivileges: (entityId: string, entityType: EntityContextType) => Promise<void>;
  /** Check if user is authorized in the current entity context */
  isAuthorizedInEntity: (privilege: any, operation?: any) => boolean;
  /** Clear entity privileges cache */
  clearEntityPrivileges: () => void;
  /** Manually set the current entity context */
  setEntityContext: (entityId: string, entityType: EntityContextType) => void;
}

const EntityPrivilegeContext = createContext<EntityPrivilegeContextType | null>(null);

interface EntityPrivilegeProviderProps {
  children: ReactNode;
}

/**
 * Entity Privilege Provider
 *
 * This provider manages dynamic entity-specific privileges (projects, samples, etc.)
 * and provides a context for privilege checking throughout the application.
 *
 * USAGE EXAMPLES:
 *
 * AUTOMATIC ENTITY CONTEXT:
 * The entity context is automatically set when a tree is initialized:
 * - Project trees: setEntityContext(projectId, 'project')
 * - Sample trees: setEntityContext(sampleId, 'sample')
 * - Other trees (publications, data packages, etc.): clearEntityPrivileges() (fallback to JWT only)
 *
 * 1. In any component within a tree, privilege checks automatically include entity-specific privileges:
 * ```tsx
 * const MyComponent = () => {
 *   const { isAuthorized } = usePrivileges();
 *
 *   // This will check both static privileges and entity-specific privileges
 *   const canEdit = isAuthorized(PrivilegeEnum.Project, 'update');
 *   const canDelete = isAuthorized(PrivilegeEnum.Sample, 'delete');
 * };
 * ```
 *
 * 2. Manual override for special cases (e.g., shipping details):
 * ```tsx
 * const ShippingDetails = ({ shipping }) => {
 *   const { setEntityContext } = useEntityPrivileges();
 *
 *   useEffect(() => {
 *     // Override the tree's entity context with the shipping's parent entity
 *     if (shipping.projectId) {
 *       setEntityContext(shipping.projectId, 'project');
 *     } else if (shipping.sampleId) {
 *       setEntityContext(shipping.sampleId, 'sample');
 *     }
 *   }, [shipping, setEntityContext]);
 * };
 * ```
 *
 * 3. Clearing context when leaving a component:
 * ```tsx
 * const MyComponent = () => {
 *   const { clearEntityPrivileges } = useEntityPrivileges();
 *
 *   useEffect(() => {
 *     return () => {
 *       // Clear context when component unmounts (optional)
 *       clearEntityPrivileges();
 *     };
 *   }, [clearEntityPrivileges]);
 * };
 * ```
 */
export const EntityPrivilegeProvider: React.FC<EntityPrivilegeProviderProps> = ({ children }) => {
  const {
    loadEntityPrivileges,
    isAuthorizedInEntity,
    clearEntityPrivileges,
    setEntityContext,
    currentEntityId,
    currentEntityType,
  } = useAuthStore();

  // Note: Entity context is now set automatically in treeStore.initializeTree()
  // based on the root node type (project or sample). This provider now just
  // exposes the context and methods for manual overrides if needed.

  const contextValue: EntityPrivilegeContextType = {
    entityId: currentEntityId,
    entityType: currentEntityType,
    loadEntityPrivileges,
    isAuthorizedInEntity,
    clearEntityPrivileges,
    setEntityContext,
  };

  return <EntityPrivilegeContext.Provider value={contextValue}>{children}</EntityPrivilegeContext.Provider>;
};

export const useEntityPrivileges = (): EntityPrivilegeContextType => {
  const context = useContext(EntityPrivilegeContext);
  if (!context) {
    throw new Error('useEntityPrivileges must be used within an EntityPrivilegeProvider');
  }
  return context;
};
